import React from 'react';
import { productTour } from './builders';

const CASH = { backTo: '/investments/cash-and-liquid', backLabel: 'Back to Cash & Liquid' };
const REAL_ESTATE = { backTo: '/investments/real-estate', backLabel: 'Back to Real Estate' };
const OVERVIEW = { backTo: '/investments', backLabel: 'Back to Investments' };

// Header totals on pages where live prices only load when the user asks for them.
const liveSummary = (button) => (
  <p><b>Invested</b> is what you paid in total. Tap <b>{button}</b> to see <b>Current Value</b>, what your holdings are worth today, with a <b>Live</b> badge. Until then you'll see <b>No live prices available</b>.</p>
);

// Header total on pages without live prices (everything is shown at cost).
const atCostSummary = (
  <p><b>Total</b> is everything you've put in, at the price you paid. Mickles doesn't fetch market prices here, so it won't go up or down with the market.</p>
);

const atCostHoldings = (what) => (
  <>
    <p>One row per {what}, combining all your entries for it, with its share of the total under <b>Allocation</b>.</p>
    <p><b>Click a row</b> to see every entry, and use the <b>⋮</b> menu there to edit or delete one. Tap a column heading to sort.</p>
  </>
);

// Product pages under Investments → Cash & Liquid, Real Estate, Crypto and Others.
export const OTHER_TOURS = {
  'inv-cash-and-liquid-savings-bank': [
    {
      title: 'Savings Bank Accounts',
      body: (
        <>
          <p>See how much money is sitting in your bank accounts, and how it's spread across banks.</p>
          <p>There's nothing to add here: this page reads the balances of your <b>Bank</b> wallets.</p>
        </>
      ),
    },
    {
      target: '[data-tour="inv-prod-summary"]',
      title: 'Your total',
      body: (
        <p><b>Total</b> adds up the balances of all your bank accounts.</p>
      ),
    },
    {
      target: '[data-tour="inv-sb-wallets"]',
      title: 'Balances come from Wallets',
      body: (
        <>
          <p>To add a bank account or update a balance, go to <b>Wallets</b>. The <b>Wallets</b> link here opens it in a new tab.</p>
          <p>Only wallets of type <b>Bank Account</b> show up here. Cash and credit cards don't.</p>
        </>
      ),
    },
    {
      target: '[data-tour="inv-prod-holdings"]',
      title: 'Account Details',
      body: (
        <p>Each bank account with its <b>Balance</b> and its share of the total under <b>Allocation</b>. Tap a column heading to sort.</p>
      ),
    },
    {
      target: '[data-tour="inv-prod-charts"]',
      title: 'Distribution',
      body: (
        <p>How your bank money is split between accounts. Hover or tap a slice to see its amount.</p>
      ),
    },
    {
      title: "You're all set",
      body: (
        <p>Keep your balances up to date in <b>Wallets</b> and this page follows. Replay this guide any time with the <b>Guide</b> button.</p>
      ),
      action: { label: CASH.backLabel, to: CASH.backTo },
    },
  ],

  'inv-cash-and-liquid-liquid-etf': productTour({
    name: 'Liquid ETFs',
    intro: (
      <>
        <p>A liquid ETF invests in very short-term, low-risk lending, so it works like a parking spot for spare cash. You buy and sell it on the stock exchange like a share.</p>
        <p>Add each purchase here and Mickles tracks what you paid and what it's worth today.</p>
      </>
    ),
    add: (
      <>
        <p>Enter the ETF, the purchase date, the <b>Price</b> per unit and the <b>Number of Units</b>. <b>Amount Invested</b> is worked out for you.</p>
        <p>Use the ETF's <b>NSE symbol</b> as its name (for example <b>LIQUIDBEES</b>) so live prices can be found. A 5-digit number is treated as a BSE code. Bought more later? Pick the same name from the list to add to it.</p>
      </>
    ),
    live: (
      <p>Fetches today's price for each ETF from the exchange and shows its current value and profit or loss. If an ETF shows no price, check that its name is the exchange symbol.</p>
    ),
    summary: liveSummary('Get Live Price'),
    ...CASH,
  }),

  'inv-cash-and-liquid-mutual-fund': productTour({
    name: 'Mutual Funds',
    intro: (
      <>
        <p>For liquid mutual funds: funds that lend money for very short periods, so they are low risk and you can usually withdraw within a day. Many people use them like a savings account.</p>
        <p>Add each purchase here and Mickles tracks what you paid and what it's worth today.</p>
      </>
    ),
    add: (
      <>
        <p>Enter the <b>Name of Fund</b>, its <b>Symbol Code</b>, the purchase date, the <b>NAV</b> (the price of one unit on that day) and the <b>Amount Invested</b>. The <b>Number of Units</b> is worked out for you.</p>
        <p>The <b>Symbol Code</b> is the fund's code on Yahoo Finance (it looks like <b>0P0000ABCD</b>). Search the fund there and copy it exactly, or live NAV won't load. Picking a fund you already have fills it in for you.</p>
      </>
    ),
    live: (
      <p>Fetches today's <b>NAV</b> for each fund using its <b>Symbol Code</b>, and shows its current value and profit or loss. If it can't find any prices, check your symbol codes.</p>
    ),
    summary: liveSummary('Get Live NAV'),
    ...CASH,
  }),

  'inv-real-estate-reit': productTour({
    name: 'REITs',
    intro: (
      <>
        <p>A REIT (Real Estate Investment Trust) owns rent-earning property such as offices and malls. Its units trade on the stock exchange like shares, so you can own a slice of property with a small amount.</p>
        <p>Add each purchase here and Mickles tracks what you paid and what it's worth today.</p>
      </>
    ),
    add: (
      <>
        <p>Enter the <b>Name of REIT</b>, its <b>Symbol Code</b>, the purchase date, the <b>Price</b> per unit and the <b>Number of Units</b>. <b>Amount Invested</b> is worked out for you.</p>
        <p>The <b>Symbol Code</b> is the REIT's NSE symbol, for example <b>EMBASSY</b> or <b>MINDSPACE</b>. <b>.NS</b> is added for you if you leave it out.</p>
      </>
    ),
    live: (
      <p>Fetches today's price for each REIT using its <b>Symbol Code</b>, and shows its current value and profit or loss. If a REIT shows no price, check its symbol. You can change it with <b>Edit Symbol</b>, shown in a moment.</p>
    ),
    summary: liveSummary('Get Live Price'),
    holdings: (
      <>
        <p>One row per REIT, combining all your purchases of it, with what you paid, its current value and your profit or loss.</p>
        <p><b>Click a row</b> to see every purchase and edit or delete one. The <b>Edit Symbol</b> button there changes the symbol for all purchases of that REIT. Tap a column heading to sort.</p>
      </>
    ),
    ...REAL_ESTATE,
  }),

  'inv-real-estate-real-estate-direct': productTour({
    name: 'Direct Real Estate',
    intro: (
      <>
        <p>Property you own yourself: a flat, house, plot or shop.</p>
        <p>Record what you've paid for each property and see how your real estate money is spread.</p>
      </>
    ),
    add: (
      <>
        <p>Enter the <b>Property Name</b>, the <b>Date of Purchase</b> and the <b>Price</b> you paid.</p>
        <p>Paying in instalments, or adding stamp duty and registration? Add each payment as a separate entry with <b>exactly the same</b> property name, and they're added together.</p>
      </>
    ),
    summary: atCostSummary,
    holdings: atCostHoldings('property'),
    charts: (
      <p>How your real estate money is split between properties. Hover or tap a slice to see its amount.</p>
    ),
    ...REAL_ESTATE,
  }),

  'inv-crypto': productTour({
    name: 'Cryptocurrency',
    intro: (
      <>
        <p>Keep a record of the crypto you've bought, such as Bitcoin or Ethereum.</p>
        <p>Values here are <b>what you paid</b>. Mickles doesn't fetch live crypto prices.</p>
      </>
    ),
    add: (
      <>
        <p>Enter the <b>Cryptocurrency Name</b>, the purchase date, the <b>Price</b> per coin in ₹ and the <b>Amount Invested</b>. The <b>Number of Units</b> (coins) is worked out for you.</p>
        <p>Bought more later? Pick the same name from the list so it's added to the same row.</p>
      </>
    ),
    summary: atCostSummary,
    holdings: atCostHoldings('coin'),
    charts: (
      <p>How your crypto money is split between coins, by amount invested. Hover or tap a slice to see its amount.</p>
    ),
    ...OVERVIEW,
  }),

  'inv-others': productTour({
    name: 'Other Investments',
    intro: (
      <>
        <p>For anything that doesn't fit the other asset classes, such as art, collectibles or unlisted shares.</p>
        <p>Values here are <b>what you paid</b>. Mickles doesn't fetch market prices for these.</p>
      </>
    ),
    add: (
      <>
        <p>Enter an <b>Investment Description</b>, the purchase date, the <b>Price</b> per unit and the number of <b>Units</b>. <b>Amount Invested</b> is worked out for you. Add any <b>Notes</b> you want to remember.</p>
        <p>Bought more of the same thing? Pick its description from the list so it's added to the same row.</p>
      </>
    ),
    summary: atCostSummary,
    holdings: atCostHoldings('investment'),
    charts: (
      <p>How your money is split between these investments. Hover or tap a slice to see its amount.</p>
    ),
    ...OVERVIEW,
  }),
};
